import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CalendarClock, CheckCircle2, HeartPulse, Info, MapPin, XCircle } from "lucide-react";
import { toast } from "sonner";
import MobileLayout from "./MobileLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { getMedicalCheckDetailDescriptionKey } from "@/lib/medical-check-display";
import {
  apiGetMedicalCheckAssignment,
  apiGetMedicalChecks,
  apiRespondToMedicalCheckAssignment,
  apiDownloadHRDocumentFile,
  type MedicalCheckStatus,
} from "@/lib/api";

const STATUS_STYLES: Record<MedicalCheckStatus, string> = {
  valid: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  due_soon: "bg-amber-500/10 text-amber-700 border-amber-200",
  expired: "bg-red-500/10 text-red-700 border-red-200",
  failed: "bg-rose-500/10 text-rose-700 border-rose-200",
  no_exam: "bg-slate-500/10 text-slate-700 border-slate-200",
};

function fmtDate(value?: string | null) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
}

export default function MobileMedicalCheck() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["medical-checks"],
    queryFn: apiGetMedicalChecks,
  });

  const myRecord = data?.employees?.[0] ?? null;
  const myAssignments = (data?.assignments ?? []).filter(a => a.status === "pending" || a.status === "accepted");
  const myHistory = (data?.assignments ?? []).filter(a => ["completed", "passed", "failed", "cancelled"].includes(a.status)).slice(0, 5);
  const legacyEmployeeId = searchParams.get("legacyEmployeeId") === "true";
  const detailQuery = useQuery({
    queryKey: ["medical-check-assignment", detailId, legacyEmployeeId],
    queryFn: () => apiGetMedicalCheckAssignment(detailId!, { legacyEmployeeId }),
    enabled: detailOpen && detailId != null,
    retry: false,
  });
  const detailAssignment = detailQuery.data ?? (legacyEmployeeId ? null : (data?.assignments ?? []).find(a => a.id === detailId) ?? null);

  // The assignee — regardless of role — is the only one who can Accept/Reject.
  // HR users on mobile may see other employees' assignments via deep links;
  // we only show the action buttons when the open assignment is theirs.
  const currentUserEmployeeId = user?.email
    ? (data?.employees ?? []).find(e => e.employeeEmail === user.email)?.employeeId ?? null
    : null;
  const isDetailAssignee = !!detailAssignment && currentUserEmployeeId != null && detailAssignment.employeeId === currentUserEmployeeId;

  // Auto-open detail dialog when ?assignment=<id> is in the URL (deep link from notification)
  useEffect(() => {
    const aId = searchParams.get("assignment");
    if (aId) {
      const id = Number(aId);
      if (Number.isInteger(id) && id > 0) {
        setDetailId(id);
        setDetailOpen(true);
      }
    }
  }, [searchParams]);

  function closeDetail() {
    setDetailOpen(false);
    setDetailId(null);
    if (searchParams.has("assignment")) {
      const next = new URLSearchParams(searchParams);
      next.delete("assignment");
      next.delete("legacyEmployeeId");
      setSearchParams(next, { replace: true });
    }
  }

  const respondMutation = useMutation({
    mutationFn: ({ id, action, rejectionReason }: { id: number; action: "accept" | "reject"; rejectionReason?: string }) =>
      apiRespondToMedicalCheckAssignment(id, { action, rejectionReason }),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.responseRecorded"));
      setRejectOpen(false);
      setRejectId(null);
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: () => toast.error(t("medicalCheckups.messages.responseFailed")),
  });

  return (
    <MobileLayout title={t("medicalCheckups.title") as string} showBack>
      <div className="p-4 space-y-4">
        {isLoading ? (
          <p className="text-center text-sm text-muted-foreground py-8">{t("common.loading")}</p>
        ) : (
          <>
            {/* Status banner */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <HeartPulse className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.status")}</p>
                    {myRecord ? (
                      <Badge variant="outline" className={STATUS_STYLES[myRecord.status]}>
                        {t(`medicalCheckups.status.${myRecord.status}`)}
                      </Badge>
                    ) : (
                      <p className="text-sm">—</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.lastCheck")}</p>
                    <p className="text-sm font-medium">{fmtDate(myRecord?.latestExamDate ?? myRecord?.lastCheckDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.nextDue")}</p>
                    <p className="text-sm font-medium">{fmtDate(myRecord?.nextDueDate)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending / accepted assignments */}
            <div>
              <h2 className="text-sm font-semibold mb-2">{t("medicalCheckups.myRecord.pendingAssignments")}</h2>
              {myAssignments.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("medicalCheckups.myRecord.noPending")}</p>
              ) : (
                <div className="space-y-2">
                  {myAssignments.map(a => (
                    <Card key={a.id}>
                      <CardContent className="p-4 space-y-3">
                        <div>
                          <p className="text-sm font-semibold">{a.institution}</p>
                          <p className="text-xs text-muted-foreground">{fmtDate(a.scheduledDate)}{a.scheduledTime ? ` · ${a.scheduledTime}` : ""}</p>
                        </div>
                        {a.status === "pending" ? (
                          <div className="grid grid-cols-2 gap-2">
                            <Button size="sm" onClick={() => respondMutation.mutate({ id: a.id, action: "accept" })}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.accept")}
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => { setRejectId(a.id); setRejectReason(""); setRejectOpen(true); }}>
                              <XCircle className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.reject")}
                            </Button>
                          </div>
                        ) : <Badge variant="outline">{t("medicalCheckups.assignmentStatus.accepted")}</Badge>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* History */}
            {myHistory.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold mb-2">{t("medicalCheckups.myRecord.history")}</h2>
                <div className="space-y-2">
                  {myHistory.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      className="block w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={() => { setDetailId(a.id); setDetailOpen(true); }}
                    >
                      <Card>
                        <CardContent className="p-3 flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm">{a.institution}</p>
                            <p className="text-xs text-muted-foreground">{fmtDate(a.scheduledDate)}</p>
                          </div>
                          <Badge
                            variant="outline"
                            className={a.status === "failed"
                              ? STATUS_STYLES.failed
                              : a.status === "cancelled"
                                ? "bg-slate-500/10 text-slate-700 border-slate-200"
                                : "bg-emerald-500/10 text-emerald-700 border-emerald-200"}
                          >
                            {a.archived ? t("medicalCheckups.assignmentStatus.archived") : t(`medicalCheckups.assignmentStatus.${a.status}`)}
                          </Badge>
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail dialog — opens via ?assignment=<id> deep link from a notification */}
      <Dialog open={detailOpen} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-primary" />{t("medicalCheckups.detailDialog.title")}
            </DialogTitle>
          </DialogHeader>
          {detailAssignment ? (
            <div className="space-y-3">
              <div className="rounded-lg border bg-primary/5 p-3 flex gap-2">
                <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-foreground/80 leading-relaxed">
                  {t(getMedicalCheckDetailDescriptionKey(detailAssignment))}
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="font-semibold">{detailAssignment.employeeName}</div>
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{fmtDate(detailAssignment.scheduledDate)}{detailAssignment.scheduledTime ? ` · ${detailAssignment.scheduledTime}` : ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{detailAssignment.institution}</span>
                </div>
                <div>
                  <Badge variant="outline" className="text-[10px]">{t(`medicalCheckups.assignmentStatus.${detailAssignment.status}`)}</Badge>
                </div>
              </div>
              {detailAssignment.notes && (
                <div className="rounded-lg border p-3 bg-muted/40">
                  <p className="text-xs font-semibold mb-1">{t("medicalCheckups.detailDialog.hrNotes")}</p>
                  <p className="text-sm whitespace-pre-wrap">{detailAssignment.notes}</p>
                </div>
              )}
              {detailAssignment.rejectionReason && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs font-semibold text-red-700">{t("medicalCheckups.rejected.reason")}</p>
                  <p className="text-sm text-red-900">{detailAssignment.rejectionReason}</p>
                </div>
              )}
              {["passed", "failed", "completed"].includes(detailAssignment.status) && (
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{t("medicalCheckups.detailDialog.result")}</p>
                  <p className="text-sm font-semibold">{t(`medicalCheckups.assignmentStatus.${detailAssignment.status}`)}</p>
                </div>
              )}
              {detailAssignment.supportingDocumentId && (
                <button
                  type="button"
                  onClick={() => {
                    void apiDownloadHRDocumentFile(
                      detailAssignment.supportingDocumentId!,
                      detailAssignment.supportingDocumentName || "document",
                    ).catch((error) => {
                      console.error("[mobile medical check document download] failed", error);
                      toast.error(t("documents.previewError.downloadFailed"));
                    });
                  }}
                  className="flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm text-primary"
                >
                  {detailAssignment.supportingDocumentName || t("medicalCheckups.detailDialog.supportingDocument")}
                </button>
              )}
            </div>
          ) : detailQuery.isLoading ? (
            <p className="text-sm text-muted-foreground py-4">{t("common.loading")}</p>
          ) : (
            <p className="text-sm text-muted-foreground py-4">
              {(detailQuery.error as any)?.code === "MEDICAL_CHECK_ACCESS_DENIED"
                ? t("medicalCheckups.detailDialog.inaccessible")
                : t("medicalCheckups.detailDialog.notFound")}
            </p>
          )}
          <DialogFooter className="flex-col gap-2">
            <Button variant="outline" onClick={closeDetail}>{t("common.close")}</Button>
            {detailAssignment && detailAssignment.status === "pending" && isDetailAssignee && (
              <div className="grid grid-cols-2 gap-2 w-full">
                <Button onClick={() => { respondMutation.mutate({ id: detailAssignment.id, action: "accept" }); closeDetail(); }}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.accept")}
                </Button>
                <Button variant="destructive" onClick={() => { setRejectId(detailAssignment.id); setRejectReason(""); setRejectOpen(true); closeDetail(); }}>
                  <XCircle className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.reject")}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">{t("medicalCheckups.rejectDialog.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("medicalCheckups.rejectDialog.description")}</p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("medicalCheckups.rejectDialog.placeholder") as string}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || respondMutation.isPending}
              onClick={() => {
                if (rejectId == null) return;
                respondMutation.mutate({ id: rejectId, action: "reject", rejectionReason: rejectReason.trim() });
              }}
            >
              {t("medicalCheckups.actions.confirmReject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
